import { Fragment, useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "react-bootstrap";
import FilterBar from "./FilterBar";
import SchedulerGrid from "./SchedulerGrid";
import ListViewTable from "./ListViewTable";
import HodAllocateModal from "../HodView/HodAllocateModal";
import ResourceTaskCard from "../ResourceView/ResourceTaskCard";
import { fetchCalendar, fetchAssignments, updateAllocationComment, patchTaskCompleted } from "../../../services/assignmentApi";
import { fetchClients, fetchClientsLookup }  from "../../../services/clientApi";
import { fetchProjects, fetchProjectsLookup } from "../../../services/projectApi";

/* ── constants ───────────────────────────────────────────────── */
const CARD_COLORS = [
  { bg: "#fff8e1", border: "#ffa726", tagBg: "#fff3cd", tagColor: "#856404" },
  { bg: "#e8f5e9", border: "#66bb6a", tagBg: "#d1e7dd", tagColor: "#0a3622" },
  { bg: "#e3f2fd", border: "#42a5f5", tagBg: "#cfe2ff", tagColor: "#084298" },
  { bg: "#fce4ec", border: "#ef5350", tagBg: "#f8d7da", tagColor: "#842029" },
  { bg: "#f3e5f5", border: "#ab47bc", tagBg: "#e9d8fd", tagColor: "#4a0e5c" },
];

/* ── logged-in user ──────────────────────────────────────────── */
function getLoggedInUser() {
  try {
    const parsed = JSON.parse(localStorage.getItem("userDetails") || "{}");
    const roleName = (parsed?.roleName || parsed?.role || "").toLowerCase();
    const isEmployee =
      roleName.includes("employee") ||
      roleName.includes("resource") ||
      roleName.includes("head") ||
      roleName.includes("hod");
    return { userId: parsed?.userId || parsed?.localId || "", isEmployee };
  } catch {
    return { userId: "", isEmployee: false };
  }
}

/* ── helpers ─────────────────────────────────────────────────── */
function getMonday(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  d.setDate(d.getDate() - day + (day === 0 ? -6 : 1));
  return d;
}

/** Format a Date as YYYY-MM-DD using LOCAL time (avoids UTC timezone shift) */
function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string as LOCAL midnight (avoids UTC shift) */
function parseLocalDate(str) {
  if (!str) return new Date(NaN);
  const [y, mo, d] = str.slice(0, 10).split("-").map(Number);
  return new Date(y, mo - 1, d);
}

function fmtDate(d) {
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function getWeekDays(weekStart) {
  const names = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
  return names.map((full, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return {
      full,
      short: full.slice(0, 3),
      date: d.toLocaleDateString("en-GB", { day: "numeric", month: "short" }),
    };
  });
}

/**
 * Map a calendar assignment to the shape SchedulerGrid / TaskCard expects.
 */
function toSchedulerTask(assignment, empName, empDesignation, weekStartDate, colorIndex) {
  const weekStartIso = isoDate(weekStartDate);
  const startIso = isoDate(parseLocalDate(assignment.startDate));
  const endIso   = isoDate(parseLocalDate(assignment.endDate));

  const from = Math.max(0, Math.round((parseLocalDate(startIso) - parseLocalDate(weekStartIso)) / 86400000));
  const to   = Math.min(4, Math.round((parseLocalDate(endIso)   - parseLocalDate(weekStartIso)) / 86400000));
  if (to < from) return null;

  return {
    id:              String(assignment.assignmentId) + colorIndex,
    assignmentId:    assignment.assignmentId,
    allocationId:    assignment.allocationId,
    category:        assignment.clientName      || "General",
    title:           assignment.projectName     || assignment.taskDescription || "Task",
    client:          assignment.clientName      || "—",
    project:         assignment.projectName     || "—",
    task:            assignment.taskDescription || "—",
    notes:           assignment.notes           || "—",
    assignedTo:      empName,
    designation:     empDesignation             || "—",
    assignedBy:      assignment.assignedBy      || "—",
    startDate:       assignment.startDate       || null,
    endDate:         assignment.endDate         || null,
    startDay:        from,
    endDay:          to,
    colorIndex:      colorIndex % CARD_COLORS.length,
    clientId:        assignment.clientId,
    projectId:       assignment.projectId,
    isTaskCompleted: assignment.isTaskCompleted || false,
  };
}

/**
 * Compute a boolean array [Mon…Fri] indicating which days this assignment covers.
 */
function getDaysArray(assignment, weekStartDate) {
  const startIso = isoDate(parseLocalDate(assignment.startDate));
  const endIso   = isoDate(parseLocalDate(assignment.endDate));
  return Array.from({ length: 5 }, (_, i) => {
    const day = new Date(weekStartDate.getFullYear(), weekStartDate.getMonth(), weekStartDate.getDate() + i);
    const dayIso = isoDate(day);
    return dayIso >= startIso && dayIso <= endIso ? 1 : 0;
  });
}

/** Map a calendar assignment to the shape ListViewTable expects. */
function toListRow(assignment, weekStartDate, idx) {
  return {
    id:           String(assignment.assignmentId) + idx,
    assignmentId: assignment.assignmentId,
    allocationId: assignment.allocationId,
    client:       assignment.clientName      || "—",
    project:      assignment.projectName     || "—",
    days:         getDaysArray(assignment, weekStartDate),
    task:         assignment.taskDescription || "—",
    comments:     assignment.notes           || "",
    userComment:  assignment.userComment     || "",
    clientId:     assignment.clientId,
    projectId:    assignment.projectId,
  };
}

/* ── component ───────────────────────────────────────────────── */
const EmployeeView = () => {
  const [weekStart, setWeekStart] = useState(() => getMonday(new Date()));

  // role
  const { userId, isEmployee } = getLoggedInUser();

  // API data
  const [schedulerTasks, setSchedulerTasks] = useState([]);
  const [listData,        setListData]       = useState([]);
  const [assignments,     setAssignments]    = useState([]);
  const [clients,         setClients]        = useState([]);
  const [projects,        setProjects]       = useState([]);
  const [loading,         setLoading]        = useState(true);
  const [error,           setError]          = useState(null);

  // filters
  const [selectedClient,  setSelectedClient]  = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  // task detail modal
  const [showDetail, setShowDetail] = useState(false);
  const [activeTask,  setActiveTask]  = useState(null);

  // comment modal (employee)
  const [showComment,  setShowComment]  = useState(false);
  const [commentRow,   setCommentRow]   = useState(null);
  const [commentText,  setCommentText]  = useState("");

  // edit job modal (non-employee)
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editData,      setEditData]      = useState(null);

  // view comment popup (admin/non-employee)
  const [commentViewRow, setCommentViewRow] = useState(null);

  // task-completed toggle state (employee only)
  const [taskCompletedSaving, setTaskCompletedSaving] = useState(false);

  /* ── derived ──────────────────────────────────────────────── */
  const weekEnd = useMemo(() => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 4);
    return d;
  }, [weekStart]);

  const days = useMemo(() => getWeekDays(weekStart), [weekStart]);

  const dateRange = useMemo(
    () => `${fmtDate(weekStart)} – ${fmtDate(weekEnd)}`,
    [weekStart, weekEnd]
  );

  const clientOptions = useMemo(() => [
    { value: null, label: "All Clients" },
    ...clients.map((c) => ({ value: c._id, label: c.clientName })),
  ], [clients]);

  const projectOptions = useMemo(() => {
    const filtered = projects.filter((p) => {
      if (p.isActive === false) return false;
      if (!selectedClient || selectedClient.value === null) return true;
      return String(p.clientId) === String(selectedClient.value);
    });
    return [
      { value: null, label: "All Projects" },
      ...filtered.map((p) => ({ value: p._id, label: p.projectName })),
    ];
  }, [projects, selectedClient]);

  const filteredTasks = useMemo(() =>
    schedulerTasks.filter((t) => {
      if (selectedClient  && t.clientId  !== selectedClient.value)  return false;
      if (selectedProject && t.projectId !== selectedProject.value) return false;
      return true;
    }),
    [schedulerTasks, selectedClient, selectedProject]
  );

  /* When a client is selected, group rows by project; otherwise by client */
  const displayTasks = useMemo(() =>
    filteredTasks.map((t) => ({
      ...t,
      category: selectedClient ? (t.project || "—") : t.category,
    })),
    [filteredTasks, selectedClient]
  );

  const displayCategories = useMemo(() =>
    [...new Set(displayTasks.map((t) => t.category))],
    [displayTasks]
  );

  const filteredList = useMemo(() =>
    listData.filter((r) => {
      if (selectedClient  && r.clientId  !== selectedClient.value)  return false;
      if (selectedProject && r.projectId !== selectedProject.value) return false;
      return true;
    }),
    [listData, selectedClient, selectedProject]
  );

  /* ── data fetching ────────────────────────────────────────── */
  const loadCalendar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rawData = await fetchCalendar(isoDate(weekStart), isoDate(weekEnd));

      // If logged-in role is employee/resource, restrict to own data only
      const calendarData = isEmployee && userId
        ? rawData.filter((emp) => String(emp.employeeId) === String(userId))
        : rawData;

      // Flatten assignments
      let colorIndex = 0;
      const tasks = [];
      const rows  = [];

      calendarData.forEach((emp) => {
        (emp.assignments || []).forEach((a) => {
          const task = toSchedulerTask(a, emp.employeeName, emp.designation, weekStart, colorIndex++);
          if (task) {
            tasks.push(task);
            rows.push(toListRow(a, weekStart, rows.length));
          }
        });
      });

      setSchedulerTasks(tasks);
      setListData(rows);
    } catch (err) {
      setError(err.message || "Failed to load schedule.");
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd, isEmployee, userId]);

  // Load dropdowns + full assignments once
  // Employees use /lookup endpoints (require only assignment view permission)
  // Non-employees use the full list endpoints (require client/project view permission)
  useEffect(() => {
    const clientFetch  = isEmployee ? fetchClientsLookup  : fetchClients;
    const projectFetch = isEmployee ? fetchProjectsLookup : fetchProjects;
    clientFetch().then(setClients).catch(() => {});
    projectFetch().then(setProjects).catch(() => {});
    if (!isEmployee) {
      fetchAssignments().then(setAssignments).catch(() => {});
    }
  }, [isEmployee]);

  useEffect(() => { loadCalendar(); }, [loadCalendar]);

  /* ── handlers ─────────────────────────────────────────────── */
  const prevWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d);
  };
  const nextWeek = () => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d);
  };

  const openDetail = (task) => { setActiveTask(task); setShowDetail(true); };

  const handleToggleTaskCompleted = async () => {
    if (!activeTask || taskCompletedSaving) return;
    const next = !activeTask.isTaskCompleted;
    setTaskCompletedSaving(true);
    // Optimistic update
    setActiveTask((prev) => ({ ...prev, isTaskCompleted: next }));
    setSchedulerTasks((prev) =>
      prev.map((t) => t.id === activeTask.id ? { ...t, isTaskCompleted: next } : t)
    );
    try {
      await patchTaskCompleted(activeTask.assignmentId, activeTask.allocationId, next);
    } catch {
      // Revert on failure
      setActiveTask((prev) => ({ ...prev, isTaskCompleted: !next }));
      setSchedulerTasks((prev) =>
        prev.map((t) => t.id === activeTask.id ? { ...t, isTaskCompleted: !next } : t)
      );
    } finally {
      setTaskCompletedSaving(false);
    }
  };;

  // Employee: open comment modal — pre-fill from userComment field
  const openComment = (row) => {
    setCommentRow(row);
    setCommentText(row ? (row.userComment || "") : "");
    setShowComment(true);
  };

  const [commentSaving, setCommentSaving] = useState(false);
  const [commentError,  setCommentError]  = useState("");

  const saveComment = async () => {
    if (!commentText.trim() || !commentRow) return;
    setCommentSaving(true);
    setCommentError("");
    try {
      await updateAllocationComment(
        commentRow.assignmentId,
        commentRow.allocationId,
        commentText.trim()
      );
      // Update local list so the new value shows without a full reload
      setListData((prev) =>
        prev.map((r) =>
          r.id === commentRow.id ? { ...r, userComment: commentText.trim() } : r
        )
      );
      setShowComment(false);
    } catch (err) {
      setCommentError(err.message || "Failed to save comment.");
    } finally {
      setCommentSaving(false);
    }
  };

  // Non-employee: open edit job modal with full assignment pre-filled
  const openEditModal = (row) => {
    const full = assignments.find((a) => String(a._id) === String(row.assignmentId));
    setEditData(full || null);
    setEditModalOpen(true);
  };

  const handleEditSaved = () => {
    setEditModalOpen(false);
    setEditData(null);
    loadCalendar();
    fetchAssignments().then(setAssignments).catch(() => {});
  };

  const detailColor = activeTask ? CARD_COLORS[activeTask.colorIndex] : CARD_COLORS[0];

  /* ── render ─────────────────────────────────────────────────── */
  return (
    <Fragment>
      <FilterBar
        dateRange={dateRange}
        onPrev={prevWeek}
        onNext={nextWeek}
        clientOptions={clientOptions}
        projectOptions={projectOptions}
        selectedClient={selectedClient}
        onClientChange={(opt) => { setSelectedClient(opt); setSelectedProject(null); }}
        selectedProject={selectedProject}
        onProjectChange={setSelectedProject}
        onClearFilters={() => { setSelectedClient(null); setSelectedProject(null); }}
      />

      {error && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3">
          <span><i className="fa fa-exclamation-circle me-2" />{error}</span>
          <button className="btn btn-sm btn-outline-danger" onClick={loadCalendar}>
            <i className="fa fa-refresh me-1" /> Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
          <p className="mt-2 text-muted" style={{ fontSize: 13 }}>Loading schedule…</p>
        </div>
      ) : (
        <>
          <SchedulerGrid
            days={days}
            categories={displayCategories}
            tasks={displayTasks}
            cardColors={CARD_COLORS}
            onTaskClick={openDetail}
            isEmployee={isEmployee}
            CardComponent={isEmployee ? undefined : ResourceTaskCard}
          />

          <ListViewTable
            data={filteredList}
            days={days}
            dateRange={dateRange}
            onPrev={prevWeek}
            onNext={nextWeek}
            clientOptions={clientOptions}
            projectOptions={projectOptions}
            selectedClient={selectedClient}
            onClientChange={(opt) => { setSelectedClient(opt); setSelectedProject(null); }}
            selectedProject={selectedProject}
            onProjectChange={setSelectedProject}
            isEmployee={isEmployee}
            onEdit={isEmployee ? openComment : openEditModal}
            onViewComment={(row) => setCommentViewRow(row)}
            onDelete={(id) => setListData((prev) => prev.filter((r) => r.id !== id))}
            onAddComment={() => openComment(null)}
          />
        </>
      )}

      {/* Edit Job Modal (non-employee users) */}
      <HodAllocateModal
        isOpen={editModalOpen}
        onClose={() => { setEditModalOpen(false); setEditData(null); }}
        editData={editData}
        onSaved={handleEditSaved}
      />

      {/* Task Detail Modal */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered>
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title style={{ fontSize: 16 }}>
            <i className="fa fa-calendar-check-o me-2 text-primary" />
            Task Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {activeTask && (() => {
            const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
            const completed = activeTask.isTaskCompleted;
            return (
              <div style={{
                borderLeft: `4px solid ${detailColor.border}`,
                background: detailColor.bg,
                borderRadius: "0 10px 10px 0",
                padding: 18,
              }}>
                {/* Client name as heading */}
                <h5 style={{ fontWeight: 700, marginBottom: 14, color: detailColor.border }}>
                  {activeTask.client}
                </h5>
                {[
                  ["Project",    activeTask.project],
                  ["Assign By",  activeTask.assignedBy],
                  ["Project Desc", activeTask.task],
                  ["Task",        activeTask.notes],
                  ["Start Date", fmtDate(activeTask.startDate)],
                  ["End Date",   fmtDate(activeTask.endDate)],
                ].map(([label, value]) => (
                  <div key={label} className="mb-2" style={{ fontSize: 13, display: "flex", gap: 6 }}>
                    <strong style={{ minWidth: 80, flexShrink: 0 }}>{label}:</strong>
                    <span style={{ color: "#495057" }}>{value || "—"}</span>
                  </div>
                ))}

                {/* Task Completed toggle — employee only */}
                {isEmployee && (
                  <div className="mt-3 pt-3" style={{ borderTop: "1px solid rgba(0,0,0,0.08)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div>
                      <strong style={{ fontSize: 13 }}>Task Completed</strong>
                      {completed && (
                        <span style={{
                          marginLeft: 8, fontSize: 10, fontWeight: 700,
                          color: "#0a3622", background: "#d1e7dd",
                          border: "1px solid #a3cfbb", borderRadius: 999,
                          padding: "2px 8px", letterSpacing: "0.04em",
                        }}>
                          DONE
                        </span>
                      )}
                    </div>
                    <div
                      role="button"
                      onClick={handleToggleTaskCompleted}
                      title={completed ? "Mark as incomplete" : "Mark as completed"}
                      style={{
                        width: 44, height: 24, borderRadius: 12,
                        background: completed ? "#198754" : "#ced4da",
                        position: "relative", cursor: taskCompletedSaving ? "not-allowed" : "pointer",
                        transition: "background 0.2s",
                        opacity: taskCompletedSaving ? 0.6 : 1,
                        flexShrink: 0,
                      }}
                    >
                      <div style={{
                        position: "absolute", top: 3,
                        left: completed ? 22 : 3,
                        width: 18, height: 18, borderRadius: "50%",
                        background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                        transition: "left 0.2s",
                      }} />
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer>
          {isEmployee && activeTask && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                const row = listData.find(
                  (r) => String(r.assignmentId) === String(activeTask.assignmentId)
                );
                setShowDetail(false);
                openComment(row || {
                  assignmentId: activeTask.assignmentId,
                  allocationId: activeTask.allocationId,
                  client:       activeTask.client,
                  project:      activeTask.project,
                  task:         activeTask.task,
                  userComment:  "",
                });
              }}
            >
              <i className="fa fa-comment me-1" />
              Comment
            </button>
          )}
          {!isEmployee && activeTask && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => {
                setShowDetail(false);
                openEditModal({ assignmentId: activeTask.assignmentId });
              }}
            >
              <i className="fa fa-pencil me-1" /> Edit
            </button>
          )}
        </Modal.Footer>
      </Modal>

      {/* View Comment Popup (admin / non-employee) */}
      <Modal show={!!commentViewRow} onHide={() => setCommentViewRow(null)} centered>
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title style={{ fontSize: 16 }}>
            <i className="fa fa-comment-o me-2 text-info" />
            Employee Comment
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {commentViewRow && (
            <>
              <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa", fontSize: 13 }}>
                <strong>{commentViewRow.client}</strong> &mdash; {commentViewRow.project}
                <div className="text-muted mt-1" style={{ fontSize: 11 }}>Task: {commentViewRow.task}</div>
              </div>
              {commentViewRow.userComment ? (
                <div style={{
                  background: "#e8f5e9", border: "1px solid #a5d6a7",
                  borderRadius: 8, padding: "12px 16px",
                  fontSize: 13, color: "#1b5e20", whiteSpace: "pre-wrap",
                }}>
                  <i className="fa fa-comment me-2 text-success" />
                  {commentViewRow.userComment}
                </div>
              ) : (
                <div style={{
                  background: "#f8f9fa", border: "1px dashed #ced4da",
                  borderRadius: 8, padding: "12px 16px",
                  fontSize: 13, color: "#adb5bd", fontStyle: "italic", textAlign: "center",
                }}>
                  No comment added by employee yet.
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => setCommentViewRow(null)}>
            Close
          </button>
        </Modal.Footer>
      </Modal>

      {/* Comment Modal (employee only) */}
      <Modal show={showComment} onHide={() => { setShowComment(false); setCommentError(""); }} centered>
        <Modal.Header closeButton className="border-bottom pb-3">
          <Modal.Title style={{ fontSize: 16 }}>
            <i className="fa fa-comment me-2 text-primary" />
            {commentRow?.userComment ? "Edit Comment" : "Add Comment"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {commentRow && (
            <div className="mb-3 p-2 rounded" style={{ background: "#f8f9fa", fontSize: 13 }}>
              <strong>{commentRow.client}</strong> &mdash; {commentRow.project}
              <div className="text-muted mt-1" style={{ fontSize: 11 }}>Task: {commentRow.task}</div>
            </div>
          )}
          <textarea
            className="form-control"
            rows={4}
            placeholder="Enter your comment…"
            value={commentText}
            onChange={(e) => { setCommentText(e.target.value); setCommentError(""); }}
          />
          {commentText.trim() === "" && (
            <div style={{ fontSize: 11, color: "#dc3545", marginTop: 4 }}>
              Comment cannot be empty.
            </div>
          )}
          {commentError && (
            <div style={{ fontSize: 11, color: "#dc3545", marginTop: 4 }}>
              <i className="fa fa-exclamation-circle me-1" />{commentError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="btn btn-secondary btn-sm" onClick={() => { setShowComment(false); setCommentError(""); }}>
            Cancel
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={saveComment}
            disabled={!commentText.trim() || commentSaving}
          >
            {commentSaving
              ? <><i className="fa fa-spinner fa-spin me-1" />Saving…</>
              : <><i className="fa fa-check me-1" />Save</>}
          </button>
        </Modal.Footer>
      </Modal>
    </Fragment>
  );
};

export default EmployeeView;